import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { serverExportOutcomeTree } from "api/api-server-outcome-transfer";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Checkbox } from "mds/components/Checkbox";
import { Dialog } from "mds/components/Dialog";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronRightIcon } from "mds/icons";
import { selectCurrentOrg, selectNestedOutcomeGroups } from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { downloadJsonFile } from "utils/file";

export function OutcomeExportDialog({ onClose }: { onClose: () => void }) {
  const org = useObjectSelector(selectCurrentOrg, ["id", "name", "short_name"]);
  const outcomeGroups = useListSelector(selectNestedOutcomeGroups, [
    "id",
    "outcomeCount",
    "title",
    "outcomeSubgroups",
    "outcomes",
  ]);
  const [explicitlySelectedOutcomeGroupIds, setExplicitlySelectedOutcomeGroupIds] = useState<
    string[] | null
  >(null);
  const [hasExportError, setHasExportError] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => exportAbortControllerRef.current?.abort(), []);

  // Keep the default "select all" choice in sync while outcome groups are loading. Once the
  // administrator changes a checkbox, preserve that explicit selection instead.
  const selectedOutcomeGroupIds = useMemo(() => {
    const explicitlySelectedOutcomeGroupIdSet =
      explicitlySelectedOutcomeGroupIds === null
        ? null
        : new Set(explicitlySelectedOutcomeGroupIds);
    const currentlySelectedOutcomeGroupIds: string[] = [];
    for (const outcomeGroup of outcomeGroups) {
      if (
        explicitlySelectedOutcomeGroupIdSet === null ||
        explicitlySelectedOutcomeGroupIdSet.has(outcomeGroup.id)
      ) {
        currentlySelectedOutcomeGroupIds.push(outcomeGroup.id);
      }
    }
    return currentlySelectedOutcomeGroupIds;
  }, [explicitlySelectedOutcomeGroupIds, outcomeGroups]);
  const selectedOutcomeGroupIdSet = new Set(selectedOutcomeGroupIds);
  const selectedOutcomeCount = outcomeGroups
    .filter((outcomeGroup) => selectedOutcomeGroupIdSet.has(outcomeGroup.id))
    .reduce((total, outcomeGroup) => total + outcomeGroup.outcomeCount, 0);

  const exportSelectedOutcomeGroups = useCallback(async () => {
    if (!org) return;
    const abortController = new AbortController();
    exportAbortControllerRef.current = abortController;
    setIsExporting(true);
    setHasExportError(false);
    try {
      const outcomeExportFile = await serverExportOutcomeTree(
        org.id,
        selectedOutcomeGroupIds,
        abortController.signal,
      );
      downloadJsonFile(outcomeExportFile, `${org.short_name}-forum-outcomes.json`);
      onClose();
    } catch {
      if (!abortController.signal.aborted) setHasExportError(true);
    } finally {
      exportAbortControllerRef.current = null;
      setIsExporting(false);
    }
  }, [onClose, org, selectedOutcomeGroupIds]);

  if (!org) return null;

  return (
    <Dialog dismissible={!isExporting} title={t("outcome_transfer.export")} open onClose={onClose}>
      <div className="mb-4 flex flex-wrap justify-between gap-2 rounded-md bg-black-tint-95 p-3">
        <span>{t("outcome_transfer.source")}</span>
        <strong className="min-w-0 break-words">{org.name}</strong>
      </div>
      <p className="mb-4">{t("outcome_transfer.select_groups")}</p>
      <div className="space-y-2">
        {outcomeGroups.map((outcomeGroup) => (
          <div
            className="flex items-start gap-2 rounded-lg border border-black-tint-90 bg-white pl-3"
            key={outcomeGroup.id}
          >
            <div className="pt-3.5">
              <Checkbox
                aria-label={outcomeGroup.title}
                checked={selectedOutcomeGroupIdSet.has(outcomeGroup.id)}
                disabled={isExporting}
                onChange={(event) => {
                  const { checked } = event.target;
                  setExplicitlySelectedOutcomeGroupIds((previousSelectedOutcomeGroupIds) => {
                    const currentlySelectedOutcomeGroupIds =
                      previousSelectedOutcomeGroupIds ?? outcomeGroups.map(({ id }) => id);
                    return checked
                      ? [...currentlySelectedOutcomeGroupIds, outcomeGroup.id]
                      : currentlySelectedOutcomeGroupIds.filter(
                          (outcomeGroupId) => outcomeGroupId !== outcomeGroup.id,
                        );
                  });
                }}
              />
            </div>
            {outcomeGroup.outcomeCount === 0 ? (
              <div className="flex min-h-11 min-w-0 flex-1 items-center gap-2 py-3 pl-2 pr-8">
                <span className="min-w-0 flex-1 break-words font-medium">{outcomeGroup.title}</span>
                <span className="rounded-full bg-black-tint-95 px-2 text-xs tabular-nums text-black-tint-40">
                  0
                </span>
              </div>
            ) : (
              <details className="group/transfer min-w-0 flex-1 break-words">
                <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-md px-2 py-3 hover:bg-black-tint-97 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue [&::-webkit-details-marker]:hidden">
                  <span className="min-w-0 flex-1 font-medium">{outcomeGroup.title}</span>
                  <span className="rounded-full bg-black-tint-95 px-2 text-xs tabular-nums text-black-tint-40">
                    {outcomeGroup.outcomeCount}
                  </span>
                  <ChevronRightIcon className="size-4 shrink-0 text-black-tint-40 transition-transform group-open/transfer:rotate-90" />
                </summary>
                <ul className="mb-3 ml-2 mr-3 space-y-3 pl-3 text-black-tint-30">
                  {outcomeGroup.outcomes.map((outcome) => (
                    <li key={outcome.id}>#{outcome.title}</li>
                  ))}
                  {outcomeGroup.outcomeSubgroups.map((outcomeSubgroup) => (
                    <li key={outcomeSubgroup.id}>
                      <p className="font-medium text-black">{outcomeSubgroup.title}</p>
                      <ul className="ml-1 mt-2 space-y-1 border-l border-black-tint-90 pl-4">
                        {outcomeSubgroup.outcomes.map((outcome) => (
                          <li key={outcome.id}>#{outcome.title}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ))}
      </div>
      <p aria-live="polite" className="mt-4">
        {t("outcome_transfer.selection", {
          groups: selectedOutcomeGroupIds.length,
          count: selectedOutcomeCount,
        })}
      </p>
      <Banner className="my-4" kind="info">
        {t("outcome_transfer.scope")}
      </Banner>
      {hasExportError && (
        <div className="my-4" role="alert">
          <Banner kind="error">{t("outcome_transfer.failed")}</Banner>
        </div>
      )}
      {isExporting && <p role="status">{t("outcome_transfer.working")}</p>}
      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <SquareButton disabled={isExporting} kind="secondary" size="m" onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>
        <SquareButton
          disabled={isExporting || selectedOutcomeGroupIds.length === 0}
          kind="accent"
          size="m"
          disableWhilePending
          onClick={exportSelectedOutcomeGroups}
        >
          {t("outcome_transfer.export_file")}
        </SquareButton>
      </div>
    </Dialog>
  );
}
